import React, { useEffect, useRef } from "react";
import { Container, Typography, Box, Grid, Paper, Divider, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// Styled components with animations and dark gradients
const ValueCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.5s ease',
  opacity: 0,
  transform: 'translateY(50px)',
  '&.animate': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.15)',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at top right, rgba(99, 102, 241, 0.2), transparent 70%)',
  },
}));

const TeamMemberCard = styled(Paper)(({ theme }) => ({
  padding: 0,
  borderRadius: '16px',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  transition: 'transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.5s ease',
  opacity: 0,
  transform: 'translateY(50px)',
  '&.animate': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.15)',
  },
}));

const TimelineCard = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(3),
  height: '100%',
  borderRadius: '16px',
  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(99, 102, 241, 0.2)',
  transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
  opacity: 0,
  transform: 'translateY(50px)',
  '&.animate': {
    opacity: 1,
    transform: 'translateY(0)',
  },
  '&:hover': {
    borderColor: '#6366f1',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), 0 0 20px rgba(99, 102, 241, 0.4)',
    background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at top right, rgba(99, 102, 241, 0.3), transparent 70%)',
  }
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: '64px',
  height: '64px',
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(99, 102, 241, 0.1) 100%)',
  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
  border: '1px solid rgba(99, 102, 241, 0.3)',
}));

// Custom hook for intersection observer
const useElementOnScreen = (options) => {
  const containerRef = useRef(null);
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        entry.target.classList.add("animate");
      }
    }, options);

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [options]);

  return containerRef;
};

const AboutUsTwo = () => {
  // Timeline refs with different delay styles
  const timelineRef1 = useElementOnScreen({ threshold: 0.1 });
  const timelineRef2 = useElementOnScreen({ threshold: 0.1 });
  const timelineRef3 = useElementOnScreen({ threshold: 0.1 });
  
  // Value card refs with different delay styles
  const valueRef1 = useElementOnScreen({ threshold: 0.1 });
  const valueRef2 = useElementOnScreen({ threshold: 0.1 });
  const valueRef3 = useElementOnScreen({ threshold: 0.1 });
  
  const ctaRef = useElementOnScreen({ threshold: 0.1 });

  // Apply transition delays using useEffect
  useEffect(() => {
    if (timelineRef1.current) timelineRef1.current.style.transitionDelay = "100ms";
    if (timelineRef2.current) timelineRef2.current.style.transitionDelay = "300ms";
    if (timelineRef3.current) timelineRef3.current.style.transitionDelay = "500ms";
    
    if (valueRef1.current) valueRef1.current.style.transitionDelay = "200ms";
    if (valueRef2.current) valueRef2.current.style.transitionDelay = "400ms";
    if (valueRef3.current) valueRef3.current.style.transitionDelay = "600ms";
  }, []);

  return (
    <Box 
    id="Home-Section-Two"
    sx={{ 
      py: { xs: 10, md: 16 }, 
      background: 'linear-gradient(180deg, #0f172a 0%, #0f1629 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decorative elements */}
      <Box sx={{ 
        position: 'absolute', 
        top: '10%', 
        right: '5%', 
        width: '500px', 
        height: '500px', 
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.15) 0%, rgba(0, 0, 0, 0) 70%)',
        zIndex: 0,
      }} />
      
      <Box sx={{ 
        position: 'absolute', 
        bottom: '15%', 
        left: '5%', 
        width: '400px', 
        height: '400px', 
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(56, 189, 248, 0.15) 0%, rgba(0, 0, 0, 0) 70%)',
        zIndex: 0,
      }} />
      
      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Section header */}
        <Box sx={{ mb: { xs: 8, md: 12 }, textAlign: 'center', maxWidth: '800px', mx: 'auto' }}>
          <Typography 
            variant="subtitle1" 
            component="span" 
            sx={{ 
              color: '#6366f1',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '1.5px',
              display: 'block',
              mb: 2 
            }}
          >
            About RockkPay
          </Typography>
          
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontWeight: 800,
              mb: 3,
              color: '#e2e8f0',
              fontSize: { xs: '2.25rem', md: '3rem' },
              lineHeight: 1.2,
            }}
          >
            Transforming the Way the World Makes Payments
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              color: '#94a3b8',
              fontWeight: 400,
              lineHeight: 1.7,
              mb: 4,
            }}
          >
            RockkPay is a leading digital payment platform designed to offer fast and secure transactions, enabling users to send money, pay bills, and manage finances effortlessly. Our mission is to create a seamless financial experience for everyone.
          </Typography>
          
          <Divider 
            sx={{ 
              width: '80px', 
              mx: 'auto', 
              borderColor: '#6366f1', 
              borderWidth: '3px',
              borderRadius: '3px'
            }} 
          />
        </Box>

        {/* Our journey timeline */}
        <Box sx={{ mb: { xs: 10, md: 14 } }}>
          <Grid container spacing={3} justifyContent="center">
            <Grid item xs={12} md={4}>
              <TimelineCard ref={timelineRef1} 
                sx={{ 
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at top left, rgba(99, 102, 241, 0.2), transparent 70%)',
                  backgroundBlendMode: 'overlay'
                }}
              >
                <Typography variant="h3" fontWeight={700} color="#e2e8f0" mb={1}>
                The Beginning
                </Typography>
                <Typography variant="h6" color="#6366f1" mb={2} fontWeight={600}>
                  Founded
                </Typography>
                <Typography variant="body1" color="#94a3b8">
                  RockkPay was founded with a vision to make payments simple, secure, and accessible for everyone.
                </Typography>
              </TimelineCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TimelineCard ref={timelineRef2} 
                sx={{ 
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at center, rgba(99, 102, 241, 0.25), transparent 70%)',
                  backgroundBlendMode: 'overlay'
                }}
              >
                <Typography variant="h3" fontWeight={700} color="#e2e8f0" mb={1}>
                Growth Phase
                </Typography>
                <Typography variant="h6" color="#6366f1" mb={2} fontWeight={600}>
                  Global Expansion
                </Typography>
                <Typography variant="body1" color="#94a3b8">
                  Expanded our services, bringing innovative payment solutions worldwide.
                </Typography>
              </TimelineCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TimelineCard ref={timelineRef3} 
                sx={{ 
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at top right, rgba(99, 102, 241, 0.2), transparent 70%)',
                  backgroundBlendMode: 'overlay'
                }}
              >
                <Typography variant="h3" fontWeight={700} color="#e2e8f0" mb={1}>
                Present Day
                </Typography>
                <Typography variant="h6" color="#6366f1" mb={2} fontWeight={600}>
                  Next Generation
                </Typography>
                <Typography variant="body1" color="#94a3b8">
                  Introduced AI-powered financial tools and blockchain integration for enhanced security and efficiency.
                </Typography>
              </TimelineCard>
            </Grid>
          </Grid>
        </Box>

        {/* Our values */}
        <Box sx={{ mb: { xs: 10, md: 14 } }}>
          <Typography 
            variant="h3" 
            component="h3" 
            fontWeight={700} 
            color="#e2e8f0" 
            textAlign="center" 
            mb={8}
            sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}
          >
            Our Core Values
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={4}>
              <ValueCard ref={valueRef1} elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at bottom right, rgba(139, 92, 246, 0.3), transparent 70%)',
                  backgroundBlendMode: 'overlay'
                }}
              >
                <IconWrapper sx={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.3) 0%, rgba(99, 102, 241, 0.1) 100%)' }}>
                  <SecurityIcon sx={{ fontSize: 32, color: '#a5b4fc' }} />
                </IconWrapper>
                <Typography variant="h5" component="h4" fontWeight={600} color="#e2e8f0" gutterBottom>
                  Uncompromising Security
                </Typography>
                <Typography variant="body1" color="#94a3b8">
                  We implement the highest standards of security measures to protect our users' financial data and transactions, making security our foremost priority.
                </Typography>
              </ValueCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ValueCard ref={valueRef2} elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at bottom right, rgba(59, 130, 246, 0.3), transparent 70%)',
                  backgroundBlendMode: 'overlay'
                }}
              >
                <IconWrapper sx={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(99, 102, 241, 0.1) 100%)' }}>
                  <SpeedIcon sx={{ fontSize: 32, color: '#93c5fd' }} />
                </IconWrapper>
                <Typography variant="h5" component="h4" fontWeight={600} color="#e2e8f0" gutterBottom>
                  Innovation & Efficiency
                </Typography>
                <Typography variant="body1" color="#94a3b8">
                  We continuously innovate to make financial transactions faster, simpler, and more efficient for individuals and businesses alike.
                </Typography>
              </ValueCard>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <ValueCard ref={valueRef3} elevation={0}
                sx={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%), radial-gradient(circle at bottom right, rgba(99, 102, 241, 0.3), transparent 70%)',
                  backgroundBlendMode: 'overlay'
                }}
              >
                <IconWrapper sx={{ background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(79, 70, 229, 0.1) 100%)' }}>
                  <SupportAgentIcon sx={{ fontSize: 32, color: '#818cf8' }} />
                </IconWrapper>
                <Typography variant="h5" component="h4" fontWeight={600} color="#e2e8f0" gutterBottom>
                  Customer-Centric Approach
                </Typography>
                <Typography variant="body1" color="#94a3b8">
                  We place our customers at the heart of everything we do, ensuring exceptional support and tailored solutions for their unique needs.
                </Typography>
              </ValueCard>
            </Grid>
          </Grid>
        </Box>
        
        {/* CTA section */}
        <Box 
          ref={ctaRef} 
          sx={{ 
            background: 'linear-gradient(135deg, #111827 0%, #0f172a 100%), radial-gradient(circle at center, rgba(99, 102, 241, 0.2), transparent 70%)',
            backgroundBlendMode: 'overlay',
            borderRadius: '24px', 
            py: { xs: 6, md: 8 }, 
            px: { xs: 4, md: 8 }, 
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            opacity: 0,
            transform: 'translateY(50px)',
            transition: 'all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            '&.animate': {
              opacity: 1,
              transform: 'translateY(0)',
            }
          }}
        >
          {/* Background elements */}
          <Box sx={{ 
            position: 'absolute', 
            top: '-30%', 
            right: '-10%', 
            width: '400px', 
            height: '400px', 
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            opacity: 0.2,
            filter: 'blur(80px)',
          }} />
          
          <Box sx={{ 
            position: 'absolute', 
            bottom: '-30%', 
            left: '-10%', 
            width: '400px', 
            height: '400px', 
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
            opacity: 0.2,
            filter: 'blur(80px)',
          }} />
          
          <Typography 
            variant="h3" 
            component="h3" 
            fontWeight={700} 
            color="white" 
            mb={3}
            sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' } }}
          >
            Ready to transform your payment experience?
          </Typography>
          
          <Typography 
            variant="h6" 
            color="#94a3b8" 
            mb={5}
            maxWidth="800px"
            mx="auto"
            fontWeight={400}
          >
            Join thousands of businesses and individuals who trust RockkPay for fast, secure, and seamless transactions worldwide.
          </Typography>
          
          <Button 
            variant="contained" 
            endIcon={<ArrowForwardIcon />}
            sx={{
              background: 'linear-gradient(45deg,rgb(246, 162, 59),rgb(250, 193, 96))',
              color: 'white',
              py: 1.5,
              px: 4,
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)',
              transition: 'all 0.3s ease',
  boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
  '&:hover': {
    background: 'linear-gradient(45deg,rgb(235, 159, 37),rgb(246, 143, 59))',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(246, 209, 59, 0.5)',
  },
            }}
          >
            Get Started with RockkPay
          </Button>
        </Box>

        {/* Add CSS for animations */}
        <style jsx global>{`
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(50px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes glowPulse {
            0% {
              box-shadow: 0 0 5px rgba(99, 102, 241, 0.5);
            }
            50% {
              box-shadow: 0 0 20px rgba(99, 102, 241, 0.8);
            }
            100% {
              box-shadow: 0 0 5px rgba(99, 102, 241, 0.5);
            }
          }
        `}</style>
      </Container>
    </Box>
  );
};

export default AboutUsTwo;